import type { LevelConfig } from "../level_config.js";
import type { PlayDialogDifficulty } from "../ui/dialog/play_dialog.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { ApiError, authGet, authGetConditional } from "../api/http_client.js";
import { NetworkManager } from "../network_manager.js";
import { setBlockMassMultiplier } from "../physics/physics_masses.js";
import { hasContentIdentity, setLevelContentIdentity } from "./level_content_identity.js";
// Leaf modules on purpose — see level_features.ts on why they may be imported
// at runtime from here while level_config may not.
import { readFeature } from "../level_features.js";
import { parsePayloadBarriers, parsePayloadLayers, parsePayloadElevator, parsePayloadHang, parsePayloadShape, parsePayloadTables, parsePayloadTilt, parsePayloadTopScale } from "../level_payload.js";

// Server-driven levels ("level_pack" content config): the segment-resolved
// config points at an immutable level release; the release blob carries the
// full level configs in play order plus a per-level shots budget computed by
// the balance curve in the admin panel. The bundled LEVELS_CONFIG stays as the
// offline / first-launch fallback, mirroring how TestLevelBridge overlays
// levels for playtesting.

interface MassOverride
{
  from: number;
  to: number;
  multiplier: number;
}

export interface LevelMassModifier
{
  multiplier: number;
  source: "base" | "override";
  range?: Pick<MassOverride, "from" | "to">;
}

interface LevelPackConfig
{
  release_uid?: string;
  mass_multiplier?: number;
  /** Level-range re-tunes on top of the base multiplier; first match wins. */
  mass_overrides?: MassOverride[];
  offline_levels_ahead?: number;
}

interface LevelBatchBlob
{
  uid: string;
  version: number;
  levels: ReleaseLevel[];
}

export class OfflineLevelUnavailableError extends Error
{
  constructor( public readonly level: number )
  {
    super( `[LevelPack] level ${level} is not cached for offline play` );
    this.name = "OfflineLevelUnavailableError";
  }
}

interface ReleaseLevel
{
  level: number;
  uid: string;
  difficulty: string;
  shots: number | null;
  config: Record<string, unknown>;
}

/** Content identity the server attaches to every level-release response (absent from older servers). */
interface ReleaseIdentity
{
  release_id?: number;
  revision?: number;
  collection?: string | null;
  collection_version?: number | null;
}

interface ReleaseBlob extends ReleaseIdentity
{
  uid: string;
  name: string;
  version: number;
  levels: ReleaseLevel[];
}

interface ManifestLevel
{
  level: number;
  difficulty: string;
  shots: number | null;
  isFeature?: boolean;
  unlocks?: string[];
}

interface ManifestBlob extends ReleaseIdentity
{
  uid: string;
  name: string;
  version: number;
  levels: ManifestLevel[];
}

interface SingleLevelBlob
{
  uid: string;
  version: number;
  level: ReleaseLevel;
}

interface LevelLruCache
{
  uid: string;
  version: number;
  entries: ReleaseLevel[];
}

const STORAGE_KEY = "bb_level_release_v1";
const MANIFEST_STORAGE_KEY = "bb_level_manifest_v1";
const LEVEL_LRU_STORAGE_KEY = "bb_level_lru_v1";
const OFFLINE_AHEAD_STORAGE_KEY = "bb_level_offline_ahead_v1";
/** uid-version of the last cache refetched only to learn its content identity. */
const IDENTITY_REFETCH_STORAGE_KEY = "bb_level_identity_refetch_v1";
const LEVEL_LRU_BASE_LIMIT = 30;
const LEVEL_LRU_KEEP_BEHIND = 5;
const DEFAULT_OFFLINE_LEVELS_AHEAD = 10;
const MAX_LEVEL_BATCH = 200;

// Mirrors DIFFICULTY_BY_NAME in test_level_bridge; duplicated on purpose so
// this module only ever `import type`s from level_config (it sits inside the
// level_config import cycle — see the client's module-cycle rule).
const DIFFICULTY_BY_NAME: Record<string, number> = {
  Common: 0,
  Hard: 1,
  SuperHard: 2,
};

export class LevelPackService
{
  private static configs = new Map<number, LevelConfig>();
  private static meta = new Map<number, ManifestLevel>();
  /** Ascending level numbers where a new block is introduced. */
  private static feature_levels: number[] = [];
  private static release_uid: string | null = null;
  private static release_version: number | null = null;
  private static mass_multiplier = 1;
  private static mass_overrides: MassOverride[] = [];
  private static refreshing: Promise<void> | null = null;
  private static startup_ready: Promise<void> | null = null;
  private static level_fetches = new Map<number, Promise<void>>();
  private static offline_levels_ahead = LevelPackService.readOfflineAhead();
  private static prefetching: Promise<void> | null = null;

  /**
   * Registers the initial network bootstrap. Gameplay waits for it so the
   * first level cannot be built from the bundled fallback while the active
   * server release is still downloading.
   */
  public static setStartupReadyPromise( promise: Promise<void> ): void
  {
    this.startup_ready = promise;
  }

  public static async waitForStartupReady(): Promise<void>
  {
    await this.startup_ready;
  }

  /**
   * A cache without content identity is refetched in full — once per release
   * version, so a server that does not send identity yet costs one download,
   * not one on every launch.
   */
  private static needsIdentityRefetch( cached: { uid: string; version: number } ): boolean
  {
    if ( hasContentIdentity( cached ) )
      return false;
    const key = `${cached.uid}-${cached.version}`;
    try
    {
      if ( localStorage.getItem( IDENTITY_REFETCH_STORAGE_KEY ) === key )
        return false;
      localStorage.setItem( IDENTITY_REFETCH_STORAGE_KEY, key );
    }
    catch {}
    return true;
  }

  public static isActive(): boolean
  {
    return this.meta.size > 0;
  }

  public static getReleaseUid(): string | null
  {
    return this.release_uid;
  }

  public static getReleaseVersion(): number | null
  {
    return this.release_version;
  }

  public static hasCachedRelease(): boolean
  {
    return this.readManifestCache() !== null || this.readCache() !== null;
  }

  public static restoreCachedForFastStart(): boolean
  {
    if ( this.isActive() )
      return true;

    const manifest = this.readManifestCache();
    if ( manifest )
    {
      this.applyManifest( manifest );
      this.restoreLruLevels();
    }
    else
    {
      const cached = this.readCache();
      if ( cached )
        this.applyBlob( cached );
    }

    const restored = this.isActive();
    return restored;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return this.configs.get( level ) ?? null;
  }

  public static isLevelCached( level: number ): boolean
  {
    return this.configs.has( level );
  }

  public static getOfflineLevelsAhead(): number
  {
    return this.offline_levels_ahead;
  }

  public static countCachedAhead( from_level: number ): number
  {
    let count = 0;
    for ( let level = from_level; this.meta.has( level ) && this.configs.has( level ); level++ )
      count++;
    return count;
  }

  public static hasLevel( level: number ): boolean
  {
    return this.meta.has( level );
  }

  /** Difficulty tier from the release; null = release inactive or level unknown. */
  public static getDifficulty( level: number ): PlayDialogDifficulty | null
  {
    const entry = this.meta.get( level );
    if ( !entry )
      return null;
    return ( DIFFICULTY_BY_NAME[entry.difficulty] ?? 0 ) as PlayDialogDifficulty;
  }

  /** Shots budget for the level from the balance curve; null = use the tier table. */
  public static getShots( level: number ): number | null
  {
    const shots = Number( this.meta.get( level )?.shots );
    return Number.isFinite( shots ) && shots > 0 ? shots : null;
  }

  /**
   * Segment mass multiplier for a specific level: the first matching range
   * override wins, otherwise the publication's base multiplier.
   */
  public static getMassMultiplierFor( level: number ): number
  {
    return this.getMassModifierFor( level ).multiplier;
  }

  /** Server mass setting selected for a level, including the matching range. */
  public static getMassModifierFor( level: number ): LevelMassModifier
  {
    for ( const range of this.mass_overrides )
    {
      if ( level >= range.from && level <= range.to )
        return { multiplier: range.multiplier, source: "override", range };
    }
    return { multiplier: this.mass_multiplier, source: "base" };
  }

  /** True when this level is one the unlock goal counts toward. */
  public static isScheduledLevel( level: number ): boolean
  {
    return this.feature_levels.includes( level );
  }

  /** The blocks that unlock exactly at this level, e.g. ["glass"]. */
  public static getUnlocks( level: number ): string[]
  {
    return this.meta.get( level )?.unlocks ?? [];
  }

  /**
   * How far the player is along the stretch between two new blocks, given the
   * level they just finished.
   *
   * The block appears AT its feature level, so it is earned by finishing the
   * level before it — that is the moment `progress === target`. `prev` is the
   * previous such moment, so the bar starts empty right after a block is handed
   * over and fills exactly once per stretch, however long the generator made it.
   *
   * Returns null when there is nothing to say: no release active, or no feature
   * level ahead. The caller keeps its own fallback rather than being handed a
   * made-up number.
   */
  public static getFeatureProgress( completed_level: number ): { progress: number; target: number; level: number; unlocks: string[] } | null
  {
    if ( this.feature_levels.length === 0 )
      return null;

    const next = this.feature_levels.find( ( level ) => level > completed_level );
    if ( next === undefined )
      return null;

    // The last moment a block was handed over — where this stretch started.
    let prev_boundary = 0;
    for ( const level of this.feature_levels )
    {
      if ( level - 1 < completed_level && level - 1 > prev_boundary )
        prev_boundary = level - 1;
    }

    const target = next - 1 - prev_boundary;
    if ( target <= 0 )
      return null;

    return {
      progress: Math.max( 0, Math.min( target, completed_level - prev_boundary ) ),
      target,
      level: next,
      unlocks: this.meta.get( next )?.unlocks ?? [],
    };
  }

  /** Highest level the active release covers (0 = release not active). */
  public static getMaxLevel(): number
  {
    let max = 0;
    for ( const level of this.meta.keys() )
      max = Math.max( max, level );
    return max;
  }

  public static async refresh(): Promise<void>
  {
    if ( this.refreshing )
      return this.refreshing;

    this.refreshing = ( async () =>
    {
      try
      {
        const pack = await getContentConfig<LevelPackConfig>( "level_pack" );
        const uid = typeof pack?.release_uid === "string" ? pack.release_uid : null;

        const multiplier = Number( pack?.mass_multiplier );
        this.mass_multiplier = Number.isFinite( multiplier ) && multiplier > 0 ? multiplier : 1;

        this.mass_overrides = Array.isArray( pack?.mass_overrides )
          ? pack.mass_overrides
              .map( ( o ) => ( { from: Number( o?.from ), to: Number( o?.to ), multiplier: Number( o?.multiplier ) } ) )
              .filter( ( o ) => Number.isFinite( o.from ) && Number.isFinite( o.to ) && Number.isFinite( o.multiplier ) && o.multiplier > 0 )
          : [];

        const ahead = Number( pack?.offline_levels_ahead );
        this.offline_levels_ahead = Number.isInteger( ahead ) && ahead >= 0 ? ahead : DEFAULT_OFFLINE_LEVELS_AHEAD;
        try { localStorage.setItem( OFFLINE_AHEAD_STORAGE_KEY, String( this.offline_levels_ahead ) ); } catch {}

        if ( !uid )
        {
          console.warn( "[LevelPack] content config carries no release_uid" );
          this.applyCachedIfAny();
          return;
        }

        const cached_manifest = this.readManifestCache();
        if ( cached_manifest && cached_manifest.uid === uid )
        {
          this.applyManifest( cached_manifest );
          this.restoreLruLevels();
        }

        if ( await this.refreshViaManifest( uid, cached_manifest ) )
          return;

        const cached = this.readCache();

        // The cache is a fast start, never the last word. A release is edited in
        // PLACE when its balance is re-tuned in the admin panel: the uid stays,
        // only `version` moves. Returning early on a uid match is what used to
        // pin a player to the shot budgets they first downloaded — permanently,
        // since this runs once per launch.
        if ( cached && cached.uid === uid )
          this.applyBlob( cached );

        // A copy cached by a build that predates content identity would be
        // revalidated as "unchanged" forever and never learn its release id —
        // so it is fetched once in full instead.
        const release_etag = cached && cached.uid === uid && typeof cached.version === "number" && !LevelPackService.needsIdentityRefetch( cached )
          ? `"${uid}-${cached.version}"`
          : null;
        const release = await authGetConditional<ReleaseBlob>( `api/player/level-release/${uid}`, release_etag );
        if ( release.status === 304 )
        {
          console.info( `[LevelPack] release ${uid} unchanged` );
          return;
        }
        const blob = release.data;
        if ( !blob || !Array.isArray( blob.levels ) )
        {
          console.warn( `[LevelPack] release ${uid} returned no level list` );
          this.applyCachedIfAny();
        }
        else
        {
          const unchanged =
            cached !== null &&
            cached.uid === blob.uid &&
            typeof blob.version === "number" &&
            cached.version === blob.version &&
            hasContentIdentity( cached ) === hasContentIdentity( blob );

          if ( !unchanged )
          {
            this.writeCache( blob );
            this.applyBlob( blob );
          }
        }
      }
      catch ( e )
      {
        // Offline or no config published for this player — the cached release
        // still applies; failing that the bundled levels carry the game.
        console.warn( "[LevelPack] refresh failed, using fallback", e );
        this.applyCachedIfAny();
      }
      finally
      {
        this.refreshing = null;
      }
    } )();

    return this.refreshing;
  }

  private static async refreshViaManifest( uid: string, cached_manifest: ManifestBlob | null ): Promise<boolean>
  {
    let manifest: ManifestBlob | null;
    try
    {
      // Same as the full release: an identity-less cache is refetched once.
      const etag = cached_manifest && cached_manifest.uid === uid && typeof cached_manifest.version === "number" && !LevelPackService.needsIdentityRefetch( cached_manifest )
        ? `"${uid}-manifest-${cached_manifest.version}"`
        : null;
      const result = await authGetConditional<ManifestBlob>( `api/player/level-release/${uid}/manifest`, etag );
      if ( result.status === 304 )
      {
        console.info( `[LevelPack] manifest ${uid} unchanged` );
        try { localStorage.removeItem( STORAGE_KEY ); } catch {}
        return true;
      }
      manifest = result.data;
    }
    catch ( e )
    {
      console.warn( "[LevelPack] manifest fetch failed, using full-release path", e );
      return false;
    }

    if ( !manifest || !Array.isArray( manifest.levels ) || manifest.levels.length === 0 )
      return false;

    const unchanged =
      cached_manifest !== null &&
      cached_manifest.uid === manifest.uid &&
      typeof manifest.version === "number" &&
      cached_manifest.version === manifest.version &&
      hasContentIdentity( cached_manifest ) === hasContentIdentity( manifest );

    if ( !unchanged )
    {
      this.writeManifestCache( manifest );
      this.applyManifest( manifest );
      this.restoreLruLevels();
    }

    try { localStorage.removeItem( STORAGE_KEY ); } catch {}
    return true;
  }

  /**
   * Guarantees the full config of `level` is in memory, then warms the next
   * level in the background — so at any point the current and the following
   * level are loaded, and only one level ever travels per request.
   */
  public static async ensureLevel( level: number ): Promise<void>
  {
    if ( !this.isActive() )
      return;

    if ( !this.configs.has( level ) && this.meta.has( level ) && !NetworkManager.isOnline() )
      throw new OfflineLevelUnavailableError( level );

    await this.fetchLevel( level );

    if ( !NetworkManager.isOnline() ) return;
    if ( this.countCachedAhead( level ) >= this.getOfflineLevelsAhead() && this.meta.has( level + 1 ) )
      this.fetchLevel( level + 1 ).catch( () => {} );
    else
      void this.prefetchOfflineWindow( level ).catch( () => {} );
  }

  public static prefetchOfflineWindow( current_level: number ): Promise<void>
  {
    if ( this.prefetching )
      return this.prefetching;

    this.prefetching = ( async () =>
    {
      if ( !this.isActive() || !NetworkManager.isOnline() )
        return;

      const uid = this.release_uid;
      const from = Math.max( 1, current_level );
      const to = from + this.offline_levels_ahead + 1;
      const missing: number[] = [];
      for ( let level = from; level <= to; level++ )
      {
        if ( this.meta.has( level ) && !this.configs.has( level ) )
          missing.push( level );
      }
      if ( missing.length === 0 )
        return;

      const batch_from = missing[0];
      const batch_to = Math.min( missing[missing.length - 1], batch_from + MAX_LEVEL_BATCH - 1 );
      let blob: LevelBatchBlob | null;
      try
      {
        blob = await authGet<LevelBatchBlob>( `api/player/level-release/${uid}/levels?from=${batch_from}&to=${batch_to}` );
      }
      catch ( e )
      {
        if ( !( e instanceof ApiError && e.status === 404 ) )
          throw e;
        console.warn( "[LevelPack] batch endpoint unavailable, warming the offline window level by level" );
        for ( const level of missing.slice( 0, LEVEL_LRU_BASE_LIMIT ) )
        {
          if ( !NetworkManager.isOnline() ) return;
          await this.fetchLevel( level );
        }
        return;
      }
      if ( !blob || !Array.isArray( blob.levels ) || uid !== this.release_uid )
        return;

      const stored: ReleaseLevel[] = [];
      for ( const entry of blob.levels )
      {
        if ( !this.meta.has( entry?.level ) || this.configs.has( entry.level ) )
          continue;
        const config = this.toConfig( entry );
        if ( !config )
          continue;
        this.configs.set( entry.level, config );
        stored.push( entry );
      }
      this.writeLruLevels( stored, current_level );
      console.info( `[LevelPack] offline window: cached ${stored.length} level(s), ${this.countCachedAhead( from )} ahead of ${from}` );

      if ( typeof blob.version === "number" && this.release_version !== null && blob.version !== this.release_version )
        void this.refresh();
    } )().finally( () => { this.prefetching = null; } );

    return this.prefetching;
  }

  private static fetchLevel( level: number ): Promise<void>
  {
    if ( !this.meta.has( level ) || this.configs.has( level ) )
      return Promise.resolve();

    const in_flight = this.level_fetches.get( level );
    if ( in_flight )
      return in_flight;

    const uid = this.release_uid;
    const fetch_promise = ( async () =>
    {
      const blob = await authGet<SingleLevelBlob>( `api/player/level-release/${uid}/level/${level}` );
      if ( !blob || !blob.level || uid !== this.release_uid )
        throw new Error( `[LevelPack] level ${level} is missing from release ${uid}` );

      const config = this.toConfig( blob.level );
      if ( !config )
        throw new Error( `[LevelPack] level ${level} of release ${uid} has no usable layout` );

      this.configs.set( level, config );
      this.writeLruLevel( blob.level );

      if ( typeof blob.version === "number" && this.release_version !== null && blob.version !== this.release_version )
      {
        console.info( `[LevelPack] release ${uid} moved to version ${blob.version} — refreshing manifest` );
        void this.refresh();
      }
    } )();

    this.level_fetches.set( level, fetch_promise );
    void fetch_promise.finally( () => this.level_fetches.delete( level ) );
    return fetch_promise;
  }

  private static applyCachedIfAny(): void
  {
    const manifest = this.readManifestCache();
    if ( manifest )
    {
      this.applyManifest( manifest );
      this.restoreLruLevels();
      return;
    }

    const cached = this.readCache();
    if ( cached )
    {
      this.applyBlob( cached );
    }
    else
    {
      console.warn( "[LevelPack] no release and no cache — falling back to bundled levels" );
      // Bundled levels belong to no release: events must stop naming the last one.
      setLevelContentIdentity( null );
      setBlockMassMultiplier( this.mass_multiplier );
    }
  }

  /**
   * The blocks the game opens with. They arrive across the first levels,
   * before the player has any sense of what "a new block" even means, so
   * celebrating them is noise — plywood is what the very first level is built
   * from, plywood2 is the same crate in another colour. Plastic is no longer
   * here: only the CONE is a starter (labeled "cone" by the generator), and
   * the schedule hands the plastic cube over later as a real unlock.
   */
  private static readonly STARTER_MATERIALS = new Set( [ "plywood", "plywood2" ] );

  /**
   * The levels worth interrupting the player for.
   *
   * Not every unlock is news. The schedule hands over a material and then, some
   * levels later, a taller version of the same one — `glass`, then `glass 1×2`,
   * then `glass 1×3`. Only the first is a new block; the rest are a block the
   * player already knows, just bigger, and announcing those turns the
   * celebration into noise.
   *
   * So a level is announced when it introduces a BLOCK not seen before — the
   * label minus its size suffix. The generator names blocks apart ("cone" vs
   * "plastic"), so the plastic cube arriving after the starter cone IS news.
   * Level 1 itself is never announced: it is the game's opening, where every
   * block is new and none of them is "a new block".
   *
   * Falls back to the plain feature marks for a release stamped from an older
   * file, which says which levels are special without naming what appears.
   */
  private static announcedLevels( sorted: number[], meta: Map<number, ManifestLevel> ): number[]
  {
    const seen = new Set<string>();
    const announced: number[] = [];

    for ( const level of sorted )
    {
      let introduces = false;
      for ( const label of meta.get( level )?.unlocks ?? [] )
      {
        // "glass 1×2" is the same block as "glass" — the size is not news.
        const words = String( label ).trim().toLowerCase().split( /\s+/ );
        const key = words.filter( ( word ) => !/^\d+[×x]\d+$/.test( word ) ).join( " " );
        if ( key.length === 0 || seen.has( key ) )
          continue;
        seen.add( key );
        if ( !LevelPackService.STARTER_MATERIALS.has( words[0] ) )
          introduces = true;
      }
      if ( introduces && level > 1 )
        announced.push( level );
    }

    return announced.length > 0
      ? announced
      : sorted.filter( ( level ) => meta.get( level )?.isFeature === true );
  }

  private static applyMeta( uid: string, version: number | null, meta: Map<number, ManifestLevel> ): void
  {
    const release_changed = this.release_uid !== uid || this.release_version !== version;
    if ( release_changed )
    {
      this.configs = new Map();
      this.level_fetches = new Map();
    }

    this.meta = meta;
    const sorted = [ ...meta.keys() ].sort( ( a, b ) => a - b );
    this.feature_levels = LevelPackService.announcedLevels( sorted, meta );
    this.release_uid = uid;
    this.release_version = version;
    setBlockMassMultiplier( this.mass_multiplier );
  }

  private static applyManifest( blob: ManifestBlob ): void
  {
    const meta = new Map<number, ManifestLevel>();
    for ( const entry of blob.levels )
    {
      if ( Number.isInteger( entry?.level ) && entry.level > 0 )
        meta.set( entry.level, entry );
    }

    if ( meta.size === 0 )
    {
      console.warn( "[LevelPack] manifest had no usable levels — keeping current config" );
      return;
    }

    this.applyMeta( blob.uid, typeof blob.version === "number" ? blob.version : null, meta );
    setLevelContentIdentity( blob );
    console.log(
      `[LevelPack] manifest active: ${blob.uid} v${blob.version} · ${meta.size} levels · ${this.feature_levels.length} feature · mass ×${this.mass_multiplier}`
    );
  }

  private static applyBlob( blob: ReleaseBlob ): void
  {
    const configs = new Map<number, LevelConfig>();
    const meta = new Map<number, ManifestLevel>();

    for ( const entry of blob.levels )
    {
      const config = this.toConfig( entry );
      if ( !config )
        continue;

      configs.set( entry.level, config );
      meta.set( entry.level, {
        level: entry.level,
        difficulty: entry.difficulty,
        shots: entry.shots,
        ...( config.isFeature ? { isFeature: true } : {} ),
        ...( ( config.unlocks?.length ?? 0 ) > 0 ? { unlocks: config.unlocks } : {} ),
      } );
    }

    if ( configs.size === 0 )
    {
      console.warn( "[LevelPack] release blob had no usable levels — keeping bundled config" );
      return;
    }

    this.applyMeta( blob.uid, typeof blob.version === "number" ? blob.version : null, meta );
    setLevelContentIdentity( blob );
    this.configs = configs;
    console.log(
      `[LevelPack] active: ${blob.uid} · ${configs.size} levels · ${this.feature_levels.length} feature · mass ×${this.mass_multiplier}`
    );
  }

  private static toConfig( entry: ReleaseLevel ): LevelConfig | null
  {
    const raw = entry.config;
    if ( !raw || typeof raw !== "object" )
      return null;

    const tables = parsePayloadTables( raw.tables, "LevelPack" );
    const layers = tables.length > 0 ? tables[0].layers : parsePayloadLayers( raw.layers );

    if ( layers.length === 0 )
      return null;

    // Carried, not interpreted: the release blob is the only place the game
    // learns which level introduces a new block, so dropping the marker here
    // would strand it one hop short of the client after surviving the whole
    // pipeline.
    const feature = readFeature( raw );

    return {
      level: entry.level,
      // Which level this position holds in the release — analytics reports it as level_uid.
      ...( typeof entry.uid === "string" && entry.uid ? { uid: entry.uid } : {} ),
      difficulty: ( DIFFICULTY_BY_NAME[entry.difficulty] ?? 0 ) as PlayDialogDifficulty,
      layers,
      ...parsePayloadTilt( raw ),
      ...parsePayloadShape( raw ),
      ...parsePayloadHang( raw ),
      ...parsePayloadElevator( raw ),
      ...parsePayloadTopScale( raw ),
      ...parsePayloadBarriers( raw, "LevelPack" ),
      ...( tables.length > 0 ? { tables } : {} ),
      ...( feature.isFeature ? { isFeature: true } : {} ),
      ...( feature.unlocks.length > 0 ? { unlocks: feature.unlocks } : {} ),
    };
  }

  private static readCache(): ReleaseBlob | null
  {
    try
    {
      const raw = localStorage.getItem( STORAGE_KEY );
      if ( !raw )
        return null;
      const blob = JSON.parse( raw ) as ReleaseBlob;
      return blob && Array.isArray( blob.levels ) ? blob : null;
    }
    catch
    {
      return null;
    }
  }

  private static writeCache( blob: ReleaseBlob ): void
  {
    try
    {
      localStorage.setItem( STORAGE_KEY, JSON.stringify( blob ) );
    }
    catch ( e )
    {
      console.warn( "[LevelPack] could not cache the release", e );
    }
  }

  private static readManifestCache(): ManifestBlob | null
  {
    try
    {
      const raw = localStorage.getItem( MANIFEST_STORAGE_KEY );
      if ( !raw )
        return null;
      const blob = JSON.parse( raw ) as ManifestBlob;
      return blob && Array.isArray( blob.levels ) ? blob : null;
    }
    catch
    {
      return null;
    }
  }

  private static writeManifestCache( blob: ManifestBlob ): void
  {
    try
    {
      localStorage.setItem( MANIFEST_STORAGE_KEY, JSON.stringify( blob ) );
    }
    catch ( e )
    {
      console.warn( "[LevelPack] could not cache the manifest", e );
    }
  }

  private static readLevelLru(): LevelLruCache | null
  {
    try
    {
      const raw = localStorage.getItem( LEVEL_LRU_STORAGE_KEY );
      if ( !raw )
        return null;
      const cache = JSON.parse( raw ) as LevelLruCache;
      return cache && Array.isArray( cache.entries ) ? cache : null;
    }
    catch
    {
      return null;
    }
  }

  private static restoreLruLevels(): void
  {
    const cache = this.readLevelLru();
    if ( !cache || cache.uid !== this.release_uid || cache.version !== this.release_version )
      return;

    for ( const entry of cache.entries )
    {
      if ( !this.meta.has( entry?.level ) || this.configs.has( entry.level ) )
        continue;
      const config = this.toConfig( entry );
      if ( config )
        this.configs.set( entry.level, config );
    }
  }

  private static writeLruLevel( entry: ReleaseLevel ): void
  {
    this.writeLruLevels( [ entry ], entry.level );
  }

  private static lruLimit(): number
  {
    return Math.max( LEVEL_LRU_BASE_LIMIT, this.offline_levels_ahead + LEVEL_LRU_KEEP_BEHIND + 2 );
  }

  private static writeLruLevels( new_entries: ReleaseLevel[], current_level: number ): void
  {
    if ( new_entries.length === 0 )
      return;
    try
    {
      const current = this.readLevelLru();
      const matches = current !== null && current.uid === this.release_uid && current.version === this.release_version;
      const incoming = new Set( new_entries.map( ( e ) => e.level ) );
      const entries = ( matches ? current.entries : [] ).filter( ( e ) => !incoming.has( e?.level ) );
      entries.push( ...new_entries );

      const limit = this.lruLimit();
      const keep_from = current_level - LEVEL_LRU_KEEP_BEHIND;
      while ( entries.length > limit )
      {
        const stale = entries.findIndex( ( e ) => e.level < keep_from );
        entries.splice( stale >= 0 ? stale : 0, 1 );
      }

      localStorage.setItem( LEVEL_LRU_STORAGE_KEY, JSON.stringify( {
        uid: this.release_uid,
        version: this.release_version,
        entries,
      } ) );
    }
    catch ( e )
    {
      console.warn( "[LevelPack] could not cache the level", e );
    }
  }

  private static readOfflineAhead(): number
  {
    try
    {
      const raw = localStorage.getItem( OFFLINE_AHEAD_STORAGE_KEY );
      const n = raw === null ? NaN : Number( raw );
      return Number.isInteger( n ) && n >= 0 ? n : DEFAULT_OFFLINE_LEVELS_AHEAD;
    }
    catch
    {
      return DEFAULT_OFFLINE_LEVELS_AHEAD;
    }
  }
}
